import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lightbulb,
  CheckSquare,
  ListTodo,
  Folders,
  Book,
  CheckCircle2,
  Clock,
  Users,
  UserCircle,
  Star,
} from 'lucide-react';
import { Task } from '../../../types/database';
import { useSupabase } from '../../../contexts/SupabaseContext';
import './Home.css';

// Helper – 30-day window
const isOlderThan30Days = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = Math.abs(now.getTime() - date.getTime());
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays > 30;
};

// Derive unique assignees with counts
const getTopAssignees = (tasks: Task[]) => {
  const map: Record<string, number> = {};
  tasks.forEach((t) => {
    if (t.assigned_to) {
      map[t.assigned_to] = (map[t.assigned_to] ?? 0) + 1;
    }
  });
  return Object.entries(map)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const {
    tasks,
    ideas,
    projects,
    loadTasks,
    loadIdeas,
    loadProjects,
  } = useSupabase();

  // Load data on mount (one-time)
  useEffect(() => {
    loadTasks();
    loadIdeas();
    loadProjects();
  }, [loadTasks, loadIdeas, loadProjects]);

  // Derived task partitions
  const todos = useMemo(() => tasks.filter((t) => t.category === 'todo'), [tasks]);
  const taskItems = useMemo(() => tasks.filter((t) => t.category === 'task'), [tasks]);
  const journalEntries = useMemo(() => tasks.filter((t) => t.category === 'journal'), [tasks]);

  const starredItems = useMemo(() => {
    const supa = tasks.filter((t) => (t as any).starred);
    let local: { id?: number; title?: string; text?: string; category?: string }[] = [];
    try {
      local = JSON.parse(localStorage.getItem('todos') || '[]').filter((todo: any) => todo.starred);
    } catch {}
    // Normalize local todo shape to match dashboard usage
    const mappedLocal = local.map((l) => ({
      id: String(l.id ?? Math.random()),
      title: l.text ?? l.title,
      category: 'todo',
    }));
    return [...supa, ...mappedLocal];
  }, [tasks]);

  // Stats object similar to the previous implementation but using real data
  const stats = useMemo(() => {
    return {
      ideas: {
        total: ideas.length,
        active: ideas.filter((i) => !i.archived).length,
      },
      todos: {
        total: todos.length,
        active: todos.filter((t) => t.status !== 'completed').length,
        completed: todos.filter((t) => t.status === 'completed').length,
        overdue: todos.filter(
          (t) => t.status !== 'completed' && isOlderThan30Days(t.created_at)
        ).length,
      },
      tasks: {
        total: taskItems.length,
        active: taskItems.filter((t) => t.status !== 'completed').length,
        completed: taskItems.filter((t) => t.status === 'completed').length,
        inProgress: taskItems.filter((t) => t.status === 'in-progress').length,
      },
      projects: {
        total: projects.length,
        active: projects.filter((p) => p.status !== 'completed').length,
        inProgress: projects.filter((p) => p.status === 'in-progress').length,
      },
      journal: {
        total: journalEntries.length,
        thisWeek: journalEntries.filter((j) => {
          const created = new Date(j.created_at);
          const now = new Date();
          const oneWeekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
          return created >= oneWeekAgo;
        }).length,
        thisMonth: journalEntries.filter((j) => {
          const created = new Date(j.created_at);
          const now = new Date();
          return (
            created.getFullYear() === now.getFullYear() &&
            created.getMonth() === now.getMonth()
          );
        }).length,
      },
    };
  }, [ideas, todos, taskItems, projects, journalEntries]);

  const topAssignees = useMemo(() => getTopAssignees(tasks), [tasks]);

  return (
    <div className="home-container">
      {/* ——— Stats grid ——— */}
      <div className="stats-grid">
        {/* Do Today (Starred) */}
        <div
          role="button"
          tabIndex={0}
          className="stat-card do-today-card"
          onClick={() => navigate('/todo')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/todo')}
        >
          <div className="stat-header">
            <Star size={20} className="star-icon" />
            <h2>Do Today</h2>
          </div>
          <div className="stat-numbers">
            <div className="stat-item highlight-warning">
              <span className="stat-value">{starredItems.length}</span>
              <span className="stat-label">Starred Items</span>
            </div>
          </div>
        </div>

        {/* Ideas */}
        <div
          role="button"
          tabIndex={0}
          className="stat-card"
          onClick={() => navigate('/ideas')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/ideas')}
        >
          <div className="stat-header">
            <Lightbulb size={20} />
            <h2>Ideas</h2>
          </div>
          <div className="stat-numbers">
            <div className="stat-item">
              <span className="stat-value">{stats.ideas.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.ideas.active}</span>
              <span className="stat-label">Active</span>
            </div>
          </div>
        </div>

        {/* Todo */}
        <div
          role="button"
          tabIndex={0}
          className="stat-card"
          onClick={() => navigate('/todo')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/todo')}
        >
          <div className="stat-header">
            <CheckSquare size={20} />
            <h2>ToDo</h2>
          </div>
          <div className="stat-numbers">
            <div className="stat-item">
              <span className="stat-value">{stats.todos.active}</span>
              <span className="stat-label">Active</span>
            </div>
            <div className="stat-item highlight-warning">
              <span className="stat-value">{stats.todos.overdue}</span>
              <span className="stat-label">Overdue</span>
            </div>
            <div className="stat-item highlight-success">
              <span className="stat-value">{stats.todos.completed}</span>
              <span className="stat-label">Done</span>
            </div>
          </div>
        </div>

        {/* Tasks */}
        <div
          role="button"
          tabIndex={0}
          className="stat-card"
          onClick={() => navigate('/tasks')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/tasks')}
        >
          <div className="stat-header">
            <ListTodo size={20} />
            <h2>Tasks</h2>
          </div>
          <div className="stat-numbers">
            <div className="stat-item">
              <span className="stat-value">{stats.tasks.active}</span>
              <span className="stat-label">Active</span>
            </div>
            <div className="stat-item highlight-info">
              <span className="stat-value">{stats.tasks.inProgress}</span>
              <span className="stat-label">In Progress</span>
            </div>
            <div className="stat-item highlight-success">
              <span className="stat-value">{stats.tasks.completed}</span>
              <span className="stat-label">Done</span>
            </div>
          </div>
        </div>

        {/* Projects */}
        <div
          role="button"
          tabIndex={0}
          className="stat-card"
          onClick={() => navigate('/projects')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/projects')}
        >
          <div className="stat-header">
            <Folders size={20} />
            <h2>Projects</h2>
          </div>
          <div className="stat-numbers">
            <div className="stat-item">
              <span className="stat-value">{stats.projects.active}</span>
              <span className="stat-label">Active</span>
            </div>
            <div className="stat-item highlight-info">
              <span className="stat-value">{stats.projects.inProgress}</span>
              <span className="stat-label">In Progress</span>
            </div>
          </div>
        </div>

        {/* Journal */}
        <div
          role="button"
          tabIndex={0}
          className="stat-card"
          onClick={() => navigate('/journal')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/journal')}
        >
          <div className="stat-header">
            <Book size={20} />
            <h2>Journal</h2>
          </div>
          <div className="stat-numbers">
            <div className="stat-item">
              <span className="stat-value">{stats.journal.thisWeek}</span>
              <span className="stat-label">This Week</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{stats.journal.thisMonth}</span>
              <span className="stat-label">This Month</span>
            </div>
          </div>
        </div>
      </div>

      {/* ——— Dashboard grid (secondary widgets) ——— */}
      <div className="dashboard-grid">
        {/* Recent completions */}
        <div className="dashboard-card">
          <div className="card-header">
            <CheckCircle2 size={18} />
            <h3>Recent Completions</h3>
          </div>
          <div className="completion-list">
            {taskItems
              .filter((t) => t.status === 'completed')
              .sort((a, b) => (a.modified_at < b.modified_at ? 1 : -1))
              .slice(0, 5)
              .map((t) => (
                <div key={t.id} className="completion-item">
                  <CheckSquare size={14} className="completion-icon" />
                  <span>{t.title}</span>
                  <span className="completion-time">{/* TODO: human time diff */}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Coming up – tasks due soon */}
        <div className="dashboard-card">
          <div className="card-header">
            <Clock size={18} />
            <h3>Coming Up</h3>
          </div>
          <div className="upcoming-list">
            {taskItems
              .filter((t) => t.status !== 'completed' && t.due_date)
              .sort((a, b) => (a.due_date! < b.due_date! ? -1 : 1))
              .slice(0, 5)
              .map((t) => (
                <div key={t.id} className="upcoming-item">
                  <span>{t.title}</span>
                  <span className="due-date">Due {new Date(t.due_date!).toLocaleDateString()}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Top assignees */}
        <div className="dashboard-card">
          <div className="card-header">
            <Users size={18} />
            <h3>Top Assignees</h3>
          </div>
          <div className="assignee-list">
            {topAssignees.map((a) => (
              <div key={a.name} className="assignee-item">
                <div className="assignee-info">
                  <UserCircle size={20} />
                  <span>{a.name}</span>
                </div>
                <span className="assignee-count">{a.count} tasks</span>
              </div>
            ))}
          </div>
        </div>

        {/* Do Today list */}
        <div className="dashboard-card" id="starred">
          <div className="card-header">
            <Star size={18} />
            <h3>Do Today</h3>
          </div>
          <div className="do-today-list">
            {starredItems.length ? (
              starredItems.map((t) => (
                <div key={t.id} className="do-today-item">
                  <span className="item-type">{t.category}</span>
                  <span className="item-title">{t.title}</span>
                </div>
              ))
            ) : (
              <p className="empty-message">No starred items</p>
            )}
          </div>
        </div>

        {/* Overdue items */}
        <div className="dashboard-card">
          <div className="card-header">
            <Clock size={18} className="overdue-icon" />
            <h3>Overdue Items</h3>
          </div>
          <div className="overdue-list">
            {todos
              .filter((t) => t.status !== 'completed' && isOlderThan30Days(t.created_at))
              .slice(0, 5)
              .map((t) => (
                <div key={t.id} className="overdue-item">
                  <span className="item-type">Todo</span>
                  <span className="item-title">{t.title}</span>
                  <span className="overdue-date">Created {new Date(t.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            {todos.filter((t) => t.status !== 'completed' && isOlderThan30Days(t.created_at)).length === 0 && (
              <p className="empty-message">No overdue items</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 